import { StorageItemFragment } from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'
import { ErrorHandler } from '@sketch/tracing'
import { useCallback } from 'react'

import {
  StorageItemTransferLocation,
  useTransferStorageItem,
} from '../../operations'
import {
  TreeNodeTypes,
  useTreeState,
} from '../../../projects/modals/ProjectPickerContent'
import { useConfirmationModal } from './useConfirmationModal'
import { canAccessListChange, getLocations } from './getLocation'
import { showMovedSuccessToast } from '../../components/MovedToasts'
import { ConfirmStorageItemTransferModal } from '../ConfirmStorageItemTransferModal'
import { useLocation, matchPath, useHistory } from 'react-router-dom'
import { routes } from '@sketch/modules-common'

export interface MoveStorageItemProps {
  data: TreeNodeTypes
}

export const useMoveStorageItem = (
  item: StorageItemFragment,
  hideModal: () => void
) => {
  const [askForConfirmation, ConfirmationModal] = useConfirmationModal()
  const { showToast } = useToast()
  const treeState = useTreeState()
  const routeLocation = useLocation()
  const history = useHistory()

  const [transferItem, { loading }] = useTransferStorageItem({
    onError: message => showToast(message, 'negative'),
  })

  const moveStorageItem = useCallback(
    (target: TreeNodeTypes) => {
      const { targetWorkspace, transferLocation } = getLocations(
        target,
        treeState
      )

      if (!transferLocation || !targetWorkspace) {
        ErrorHandler.shouldNeverHappen('target location is not defined')
        return
      }

      const location: StorageItemTransferLocation = {
        source: {
          workspaceId: transferLocation.source.workspace.identifier,
          projectId: transferLocation.source.project?.identifier,
        },
        destination: {
          workspaceId: transferLocation.destination.workspace.identifier,
          projectId: transferLocation.destination.project?.identifier,
        },
      }

      const transfer = () =>
        transferItem({
          item,
          location,
          onCompleted: () => {
            // if user is looking at the project that is being moved
            // to another workspace, we need to redirect somewhere else
            // see: https://linear.app/sketch/issue/SWEB-994/error-screen-shown-after-moving-project-while-inside-it
            if (item.__typename === 'Project') {
              const matchesCurrentUrlRoute = matchPath(
                routeLocation.pathname,
                routes.WORKSPACE_PROJECT.create({
                  workspaceId: location.source.workspaceId,
                  projectId: item.identifier,
                })
              )

              if (
                matchesCurrentUrlRoute &&
                location.source.workspaceId !== location.destination.workspaceId
              ) {
                history.replace({
                  pathname: routes.WORKSPACE_SHARES.create({
                    workspaceId: location.source.workspaceId,
                  }),
                })
              }
            }

            showMovedSuccessToast({
              item,
              location: transferLocation,
              showToast,
            })
            hideModal()
          },
        })

      const shouldAskForConfirmation = canAccessListChange(target, treeState)

      if (shouldAskForConfirmation) {
        askForConfirmation(
          ConfirmStorageItemTransferModal,
          {
            currentLocation: transferLocation.source,
            destination: transferLocation.destination,
            item,
          },
          () => transfer()
        )
        return
      }

      transfer()
    },
    [
      askForConfirmation,
      hideModal,
      history,
      item,
      routeLocation.pathname,
      showToast,
      transferItem,
      treeState,
    ]
  )

  return [moveStorageItem, { loading, ConfirmationModal }] as const
}
